// ADR 23.9.26w §4 — what the offline queue holds: four flashcard writes, keyed
// the way the native queues coalesce them.
//
// A second pin or snooze of one target **replaces** the first (last intent
// wins, `pin_queue.rs` / `card_pin_queue.rs` / `snooze/queue.rs`); reviews are
// keyed by their own `opId` and so never coalesce, as `review_queue.rs` keys
// each review by a fresh UUID.

import type { SnoozeBody } from '../../api/flashcards'

export type OpKind = 'review' | 'card_pin' | 'list_pin' | 'snooze'

/**
 * The native drain order: iOS runs its four drainers in this sequence when
 * connectivity returns (`AppNavigation.swift:116-120`).
 */
const KIND_ORDER: readonly OpKind[] = ['review', 'list_pin', 'card_pin', 'snooze']

/** One write, as a caller hands it to the queue. */
export type OpPayload =
  | { kind: 'review'; opId: string; cardId: string; quality: number }
  | { kind: 'card_pin'; cardId: string; pinned: boolean }
  | { kind: 'list_pin'; listId: string; pinned: boolean }
  | { kind: 'snooze'; cardId: string; body: SnoozeBody }

/**
 * One stored op. `rev` is fresh on every put, so the drainer can tell the
 * record it sent from one the learner replaced while the send was in flight.
 */
export type QueuedOp = OpPayload & {
  userId: string
  key: string
  createdAt: number
  rev: string
}

/** A row as IndexedDB returns it: only the compound key is guaranteed. */
export interface StoredRecord {
  userId: string
  key: string
  rev?: unknown
  kind?: unknown
  [field: string]: unknown
}

/** The coalescing key: `review:<opId>`, `card_pin:<cardId>`, `list_pin:<listId>`, `snooze:<cardId>`. */
export function opKey(op: OpPayload): string {
  switch (op.kind) {
    case 'review':
      return `review:${op.opId}`
    case 'list_pin':
      return `list_pin:${op.listId}`
    case 'card_pin':
    case 'snooze':
      return `${op.kind}:${op.cardId}`
  }
}

const isString = (value: unknown): value is string => typeof value === 'string'
const isBool = (value: unknown): value is boolean => typeof value === 'boolean'

const PAYLOAD_GUARDS: Record<OpKind, (record: StoredRecord) => boolean> = {
  review: (r) => isString(r.opId) && isString(r.cardId) && typeof r.quality === 'number',
  card_pin: (r) => isString(r.cardId) && isBool(r.pinned),
  list_pin: (r) => isString(r.listId) && isBool(r.pinned),
  snooze: (r) => isString(r.cardId) && typeof r.body === 'object' && r.body !== null,
}

function payloadIsReadable(record: StoredRecord): boolean {
  const kind = record.kind
  return isString(kind) && Object.hasOwn(PAYLOAD_GUARDS, kind) && PAYLOAD_GUARDS[kind as OpKind](record)
}

/**
 * The kind guard the drainer applies before sending. A record that fails it —
 * a future version's shape, or a corrupted row — is discarded as `unreadable`.
 */
export function isQueuedOp(record: StoredRecord): record is StoredRecord & QueuedOp {
  return isString(record.rev) && typeof record.createdAt === 'number' && payloadIsReadable(record)
}

/** Kind (native drain order), then oldest first, then key — a total order. */
export function compareOps(a: QueuedOp, b: QueuedOp): number {
  const byKind = KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind)
  if (byKind !== 0) return byKind
  if (a.createdAt !== b.createdAt) return a.createdAt - b.createdAt
  return a.key < b.key ? -1 : a.key > b.key ? 1 : 0
}
